import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry } from "ag-grid-community";
import { AllCommunityModule } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
// TODO: use theming API
import "./VirtualizedDataGrid.css";
import type {
    ColDef,
    GridReadyEvent,
    CellEditingStoppedEvent,
    GridApi,
    FilterChangedEvent,
    IDatasource,
    IGetRowsParams
} from "ag-grid-community";
import { PlusCircleIcon, TableCellsIcon } from "@heroicons/react/24/outline";
import { Button } from "./Button";
import { ConfirmModal } from "./ConfirmModal";
import { newUuid, classNames } from "../lib/utils";
import {
    ITableRowFilter,
    TableRowFilterOperator,
    IPrimitiveType
} from "../lib/backend/extractions.types.generated";
import { MultiselectInputField } from "./MultiselectInputField";

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

export interface ITableRowDisplay {
    data: Record<string, IPrimitiveType>;
    uuid?: string;
}

interface VirtualizedDataGridProps {
    headers: { uuid: string; name: string; pinned?: "left" | "right", is_hidden?: boolean }[];
    total_rows?: number;
    onLoadData: (offset: number, limit: number, filters: ITableRowFilter[]) => Promise<{ rows: ITableRowDisplay[] }>;
    onAddRow?: (row: string[]) => Promise<void>;
    onUpdateRow?: (uuid: string, row: string[]) => Promise<void>;
    onDeleteRow?: (uuid: string) => Promise<void>;
    onRowSelect?: (row: ITableRowDisplay) => void;
    initial_filters?: ITableRowFilter[];
    readonly?: boolean;
    page_size?: number;
    show_actions_header?: boolean;
    show_row_numbers?: boolean;
    full_height?: boolean;
    can_render?: boolean;
}

export function VirtualizedDataGrid({
    headers,
    total_rows,
    onLoadData,
    onAddRow,
    onUpdateRow,
    onDeleteRow,
    onRowSelect,
    initial_filters,
    readonly = false,
    page_size = 100,
    show_actions_header = false,
    show_row_numbers = false,
    full_height = false,
    can_render = true
}: VirtualizedDataGridProps) {

    const [grid_api, setGridApi] = useState<GridApi | null>(null);
    const [show_delete_confirm, setShowDeleteConfirm] = useState(false);
    const [row_to_delete, setRowToDelete] = useState<string | null>(null);
    const [new_rows, setNewRows] = useState<any[]>([]);
    const [is_loading, setIsLoading] = useState(true);
    const [show_no_data, setShowNoData] = useState(false);
    const [last_filter_model, setLastFilterModel] = useState<any>(null);
    const [display_filters, setDisplayFilters] = useState<{ column_name: string, value: string, header_uuid: string }[]>([]);

    // Use ref to always have the latest new_rows value in the datasource
    const new_rows_ref = useRef<any[]>([]);
    new_rows_ref.current = new_rows;

    // Convert backend filters to AG Grid filter format
    const convertBackendFiltersToAgGridFilters = useCallback((backend_filters: ITableRowFilter[]) => {
        const ag_grid_filters: any = {};

        backend_filters.forEach(filter => {
            const header = headers.find(h => h.uuid === filter.column_uuid);
            if (!header) return;

            // Convert backend operator to AG Grid format
            let ag_filter_type = "text";
            let ag_operator = "contains";
            let filter_value = filter.values[0];

            switch (filter.operator) {
                case "eq":
                    // Use "blank" for empty string equality
                    if (filter_value === "") {
                        ag_operator = "blank";
                    } else {
                        ag_operator = "equals";
                    }
                    break;
                case "ne":
                    // Use "notBlank" for empty string inequality
                    if (filter_value === "") {
                        ag_operator = "notBlank";
                    } else {
                        ag_operator = "notEqual";
                    }
                    break;
                case "text_contained_in":
                    ag_operator = "contains";
                    break;
                case "gt":
                    ag_operator = "greaterThan";
                    ag_filter_type = "number";
                    break;
                case "gte":
                    ag_operator = "greaterThanOrEqual";
                    ag_filter_type = "number";
                    break;
                case "lt":
                    ag_operator = "lessThan";
                    ag_filter_type = "number";
                    break;
                case "lte":
                    ag_operator = "lessThanOrEqual";
                    ag_filter_type = "number";
                    break;
                default:
                    ag_operator = "contains";
            }

            const ag_filter = {
                filterType: ag_filter_type,
                type: ag_operator,
                // Don't include filter property for blank/notBlank
                ...(ag_operator !== "blank" && ag_operator !== "notBlank" && { filter: filter_value })
            };
            ag_grid_filters[header.uuid] = ag_filter;
        });

        return ag_grid_filters;
    }, [headers]);

    // Convert AG Grid filter model to TableRowFilter
    const convertAgGridFiltersToBackendFilters = useCallback((filter_model: any): ITableRowFilter[] => {
        const filters: ITableRowFilter[] = [];

        Object.entries(filter_model).forEach(([field, filter_data]: [string, any]) => {

            const header = headers.find(h => h.uuid === field);
            if (!header) return;

            // AG Grid text filter structure: { filterType: "text", type: "contains", filter: "value" }
            if (filter_data.filterType === "text" || filter_data.type === "text") {
                // Text filter
                let operator: TableRowFilterOperator;
                let values: IPrimitiveType[] = [];

                // Use the "type" field which contains the actual filter operation
                const filter_type = filter_data.type;

                switch (filter_type) {
                    case "equals":
                        operator = "eq";
                        if (filter_data.filter) values = [filter_data.filter];
                        break;
                    case "notEqual":
                        operator = "ne";
                        if (filter_data.filter) values = [filter_data.filter];
                        break;
                    case "contains":
                        operator = "text_contained_in";
                        if (filter_data.filter) values = [filter_data.filter];
                        break;
                    case "blank":
                        // For blank filter, filter for empty strings
                        operator = "eq";
                        values = [null];
                        break;
                    case "notBlank":
                        // For not blank filter, use not equals with empty string
                        operator = "ne";
                        values = [null];
                        break;
                    default:
                        operator = "text_contained_in";
                        if (filter_data.filter) values = [filter_data.filter];
                }

                // Only add filter if we have values (except for blank/notBlank which handle empty values)
                if (values.length > 0 || filter_type === "blank" || filter_type === "notBlank") {
                    filters.push({
                        column_uuid: header.uuid,
                        operator,
                        values,
                        text_filter_options: {
                            case_insensitive: true
                        }
                    });
                }
            } else if (filter_data.filterType === "number" && (filter_data.filter !== undefined || filter_data.filterTo !== undefined)) {
                // Number filter
                let operator: TableRowFilterOperator = "eq";
                let values: (string | number)[] = [];

                const filter_type = filter_data.type;
                switch (filter_type) {
                    case "equals":
                        operator = "eq";
                        values = [filter_data.filter];
                        break;
                    case "notEqual":
                        operator = "ne";
                        values = [filter_data.filter];
                        break;
                    case "lessThan":
                        operator = "lt";
                        values = [filter_data.filter];
                        break;
                    case "lessThanOrEqual":
                        operator = "lte";
                        values = [filter_data.filter];
                        break;
                    case "greaterThan":
                        operator = "gt";
                        values = [filter_data.filter];
                        break;
                    case "greaterThanOrEqual":
                        operator = "gte";
                        values = [filter_data.filter];
                        break;
                    case "inRange":
                        // For range filters, use >= for the lower bound
                        if (filter_data.filter !== undefined) {
                            filters.push({
                                column_uuid: header.uuid,
                                operator: "gte",
                                values: [filter_data.filter]
                            });
                        }
                        if (filter_data.filterTo !== undefined) {
                            filters.push({
                                column_uuid: header.uuid,
                                operator: "lte",
                                values: [filter_data.filterTo]
                            });
                        }
                        return; // Early return since already added the filters
                    default:
                        operator = "eq";
                        values = [filter_data.filter];
                }

                if (values.length > 0) {
                    filters.push({
                        column_uuid: header.uuid,
                        operator,
                        values
                    });
                }
            }
        });

        return filters;
    }, [headers]);

    // Create datasource for infinite row model
    const getDatasource = useCallback((): IDatasource => ({
        getRows: async (params: IGetRowsParams) => {
            // Check if filters have changed by comparing with last filter model
            const current_filter_json = JSON.stringify(params.filterModel || {});
            const last_filter_json = JSON.stringify(last_filter_model || {});
            const filters_changed = current_filter_json !== last_filter_json;

            // Show loading only for initial load (offset 0 + no previous filters) or when filters change
            const is_initial_load = params.startRow === 0 && last_filter_model === null;
            const is_filter_change = params.startRow === 0 && filters_changed && last_filter_model !== null;
            const should_show_loading = is_initial_load || is_filter_change;

            try {

                // Update last filter model
                setLastFilterModel(params.filterModel || {});

                // Show loading state only for initial load or filter changes
                if (should_show_loading) {
                    setIsLoading(true);
                }

                // Convert AG Grid filters to backend format
                const backend_filters = convertAgGridFiltersToBackendFilters(params.filterModel || {});

                // Calculate offset and limit
                const offset = params.startRow;
                const limit = params.endRow - params.startRow;

                // Load data from backend
                const result = await onLoadData(offset, limit, backend_filters);

                // Transform data for ag-Grid
                const transformed_data = result.rows.map((row, index) => {
                    const uuid = row.uuid || `row_${offset + index}`;
                    const transformed: any = {
                        uuid: uuid,
                        rowNumber: offset + index + 1,
                        ...row.data
                    };
                    return transformed;
                });

                // Add any new rows at the beginning (only on first page)
                const current_new_rows = new_rows_ref.current;
                const all_rows = offset === 0 ? [...current_new_rows, ...transformed_data] : transformed_data;

                // Determine if we have more data
                let last_row = -1;

                // Check if got fewer rows than requested - reached the end
                if (transformed_data.length < limit) {
                    // Reached the end of available data
                    last_row = offset + all_rows.length;
                } else if (backend_filters.length === 0 && total_rows !== undefined) {
                    // Only use total_rows when no filters are applied (unfiltered data)
                    last_row = total_rows + current_new_rows.length;
                }
                // Otherwise, last_row stays -1, indicating more data might be available

                params.successCallback(all_rows, last_row);

                // Handle no data state (only for initial/filter loads)
                if (should_show_loading) {
                    if (all_rows.length === 0 && offset === 0) {
                        // No data at all - show custom no data overlay
                        setShowNoData(true);
                        if (grid_api) grid_api.hideOverlay();
                    } else {
                        // Data loaded successfully - hide no data overlay
                        setShowNoData(false);
                        if (grid_api) grid_api.hideOverlay();
                    }
                }
            } catch (error) {
                console.error("Error in infinite datasource:", error);
                params.failCallback();

                // Show no data overlay on error (only for initial/filter loads)
                if (should_show_loading) {
                    setShowNoData(true);
                    if (grid_api) grid_api.hideOverlay();
                }
            } finally {
                // Hide loading state only if we showed it
                if (should_show_loading) {
                    setIsLoading(false);
                }
            }
        }
    }), [onLoadData, total_rows, convertAgGridFiltersToBackendFilters, grid_api, last_filter_model, setShowNoData]);

    const addNewRow = useCallback(() => {
        if (readonly || !onAddRow) {
            return;
        }

        const new_row_data = {
            uuid: newUuid(),
            isNewRow: true,
            rowNumber: "*",
            ...headers.reduce((acc, header) => {
                acc[header.uuid] = "";
                return acc;
            }, {} as any)
        };

        setNewRows(prev => {
            const updated = [new_row_data, ...prev];
            return updated;
        });

        // Refresh the grid to show the new row
        if (grid_api) {
            grid_api.refreshInfiniteCache();
        }
    }, [readonly, onAddRow, headers, grid_api]);

    // Handle delete row
    const handleDeleteRow = useCallback(async (uuid: string) => {
        if (!onDeleteRow || readonly) return;

        try {
            await onDeleteRow(uuid);

            // Remove from new rows if there
            setNewRows(prev => prev.filter(row => row.uuid !== uuid));

            // Refresh the grid data
            if (grid_api) {
                grid_api.refreshInfiniteCache();
            }

        } catch (error) {
            console.error("Error deleting row:", error);
        }
    }, [onDeleteRow, readonly, grid_api]);

    // Initiate delete confirmation
    const initiateDeleteRow = useCallback((uuid: string) => {
        setRowToDelete(uuid);
        setShowDeleteConfirm(true);
    }, []);

    // Handle delete confirmation modal response
    const onDeleteConfirm = useCallback(async (should_delete: boolean) => {
        setShowDeleteConfirm(false);

        if (should_delete && row_to_delete) {
            await handleDeleteRow(row_to_delete);
        }

        setRowToDelete(null);
    }, [row_to_delete, handleDeleteRow]);

    // Expose delete function globally for button clicks
    useEffect(() => {
        (window as any).deleteRow = initiateDeleteRow;
        return () => {
            delete (window as any).deleteRow;
        };
    }, [initiateDeleteRow]);

    // Handle clicking outside cells to clear focus
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Element;
            // Check if click is NOT on a cell or editor popup
            if (!target.closest(".ag-cell") &&
                !target.closest(".ag-cell-edit-input") &&
                !target.closest(".ag-large-text-input") &&
                !target.closest(".ag-popup-editor") &&
                !target.closest(".ag-dialog") &&
                !target.closest(".ag-popup")) {
                grid_api?.clearFocusedCell();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [grid_api]);

    // Handle window resize to adjust column widths
    useEffect(() => {
        const handleResize = () => {
            if (grid_api) {
                // Small delay to ensure the grid container has resized
                setTimeout(() => {
                    grid_api.sizeColumnsToFit();
                }, 100);
            }
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [grid_api]);

    // Column definitions
    const column_defs: ColDef[] = useMemo(() => {
        const cols: ColDef[] = [];

        // Add simple delete action column
        if (!readonly && onDeleteRow) {
            cols.push({
                field: "delete",
                headerName: "",
                width: 40,
                pinned: "left",
                sortable: false,
                filter: false,
                resizable: false,
                editable: false,
                cellClass: "delete-cell",
                valueGetter: (params: any) => {
                    // Don't show delete for new rows
                    return params.data?.isNewRow ? "" : "x";
                },
                onCellClicked: (params: any) => {
                    if (!params.data?.isNewRow && params.data?.uuid) {
                        initiateDeleteRow(params.data.uuid);
                    }
                }
            });
        }

        // Add row number column (pinned left)
        if (show_row_numbers) {
            cols.push({
                field: "rowNumber",
                headerName: "#",
                width: 75,
                minWidth: 75,
                pinned: "left",
                sortable: false,
                filter: false,
                resizable: false,
                editable: false,
                cellClass: "row-number-cell"
            });
        }

        // Add data columns
        const data_cols: ColDef[] = headers.map((header) => ({
            field: header.uuid,
            headerName: header.name,
            hide: header.is_hidden,
            cellEditor: "agLargeTextCellEditor",
            cellEditorPopup: true,
            cellEditorParams: {
                maxLength: 10000
            },
            // display newlines as ↵
            cellRenderer: (params: any) => {
                if (params.value == null || params.value === "") {
                    return "";
                }
                const cell_value = String(params.value);
                if (cell_value.includes("\n")) {
                    const parts = cell_value.split("\n");
                    return (
                        <span title={cell_value}>
                            {parts.map((part, index) => (
                                <span key={index}>
                                    {part}
                                    {index < parts.length - 1 && (
                                        <span className="text-gray-300"> ↵ </span>
                                    )}
                                </span>
                            ))}
                        </span>
                    );
                }
                return cell_value;
            },
            flex: 1,
            minWidth: 150,
            editable: !readonly && !header.is_hidden,
            sortable: false, // TODO: Add sorting in the backend
            filter: "agTextColumnFilter", // Use text filter explicitly
            pinned: header.pinned,
            filterParams: {
                buttons: ["apply", "reset"],
                closeOnApply: true, // Automatically close filter popup when Apply/Reset is clicked
                // Disable multiple filter conditions (AND/OR chaining) by limiting to 1 condition
                maxNumConditions: 1,
                // Only include supported filter options that map to backend operators
                filterOptions: [
                    "contains",     // maps to text_contained_in
                    "equals",       // maps to eq
                    "notEqual",     // maps to ne
                    "blank",        // handled by checking for null/empty
                    "notBlank"      // handled by checking for non-null/non-empty
                    // Disabled: startsWith, endsWith, notContains (not supported by backend)
                ],
                defaultOption: "contains"
            },
            resizable: true,
            cellClass: (params) => {
                let classes = [];

                // Highlight new rows
                if (params.data?.isNewRow) {
                    classes.push("new-row-cell");
                }

                // Add light gray background for pinned columns
                if (header.pinned) {
                    classes.push("pinned-cell");
                }

                return classes.join(" ");
            }
        }));

        cols.push(...data_cols);
        return cols;
    }, [headers, readonly, onDeleteRow, initiateDeleteRow, show_row_numbers]);

    // Handle cell editing
    const onCellEditingStopped = useCallback(async (event: CellEditingStoppedEvent) => {
        if (!event.data || readonly) return;

        // Handle new row - add to backend when any cell is edited
        if (event.data.isNewRow) {
            const row = headers.map((header) => event.data[header.uuid] || "");

            // Check if any cell has content
            const has_content = row.some(cell => cell?.toString().trim() !== "");

            if (has_content && onAddRow) {
                try {
                    await onAddRow(row);

                    // Remove from new rows and refresh grid
                    setNewRows(prev => prev.filter(r => r.uuid !== event.data.uuid));
                    if (grid_api) {
                        grid_api.refreshInfiniteCache();
                    }

                } catch (error) {
                    console.error("Error adding row:", error);
                    // Optionally revert the changes or show error message
                }
            }
            return;
        }

        // Handle existing row update
        if (onUpdateRow && event.data.uuid && !event.data.isNewRow && event.newValue !== event.oldValue) {
            const row = headers.map((header) => event.data[header.uuid] || "");
            try {
                await onUpdateRow(event.data.uuid, row);
            } catch (error) {
                console.error("Error updating row:", error);
                // Revert the change
                event.data[event.colDef?.field || ""] = event.oldValue;
                if (grid_api) {
                    grid_api.refreshCells({ rowNodes: [event.node!] });
                }
            }
        }
    }, [headers, readonly, onAddRow, onUpdateRow, grid_api]);

    // Handle grid ready
    const onGridReady = useCallback((params: GridReadyEvent) => {
        console.log("Grid API ready");
        setGridApi(params.api);

        // Apply initial filters if provided
        if (initial_filters && initial_filters.length > 0) {
            const ag_grid_filters = convertBackendFiltersToAgGridFilters(initial_filters);
            params.api.setFilterModel(ag_grid_filters);
        }

        // Set the datasource
        params.api.setGridOption("datasource", getDatasource());

        // Size columns to fit the grid width
        params.api.sizeColumnsToFit();
    }, [getDatasource, initial_filters, convertBackendFiltersToAgGridFilters]);

    // Fallback for initial filters (in some cases, the grid may not be ready when the component is mounted)
    const onFirstDataRendered = useCallback(() => {
        if (initial_filters && initial_filters.length > 0 && grid_api && grid_api.getFilterModel() === null) {
            const ag_grid_filters = convertBackendFiltersToAgGridFilters(initial_filters);
            grid_api?.setFilterModel(ag_grid_filters);
        }
    }, [initial_filters, convertBackendFiltersToAgGridFilters]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle filter changes - refresh infinite cache to trigger server-side filtering
    const onFilterChanged = useCallback((event: FilterChangedEvent) => {
        // Update display filters when AG Grid filters change
        const filter_model = event.api.getFilterModel();
        const new_display_filters: { column_name: string, value: string, header_uuid: string }[] = [];

        Object.entries(filter_model).forEach(([header_uuid, filter_data]: [string, any]) => {
            const header = headers.find(h => h.uuid === header_uuid);
            if (!header) return;

            let filter_value = filter_data.filter !== undefined ? String(filter_data.filter) : "";
            if (filter_value.length > 10) {
                filter_value = filter_value.substring(0, 10) + "...";
            }
            const operator = filter_data.type || "contains";
            const display_value = operator === "blank" || operator === "notBlank" ?
                (operator === "blank" ? "is 'blank'" : operator === "notBlank" ? "is 'not blank'" : "") :
                `${operator} '${filter_value}'`;

            new_display_filters.push({
                column_name: header.name,
                value: display_value,
                header_uuid: header_uuid
            });
        });

        setDisplayFilters(new_display_filters);

        // Hide no data overlay when filters change (data will be reloaded)
        setShowNoData(false);

        // The filter change will be detected in the datasource by comparing filter models
        event.api.refreshInfiniteCache();
    }, [headers]);

    // Handle row click for selection
    const onRowClicked = useCallback((event: any) => {
        if (!onRowSelect || !event.data) return;

        // Don't trigger selection if clicking on delete button or editing cells
        if (event.colDef?.field === "delete" || event.data.isNewRow) return;

        onRowSelect(event);
    }, [onRowSelect]);

    // Handle removing a filter
    const handleRemoveFilter = useCallback((idx: number) => {
        if (!grid_api || idx >= display_filters.length) return;

        const filter_to_remove = display_filters[idx];
        const current_filter_model = grid_api.getFilterModel();

        // Remove the specific filter from the model
        delete current_filter_model[filter_to_remove.header_uuid];

        // Apply the updated filter model
        grid_api.setFilterModel(current_filter_model);
    }, [grid_api, display_filters]);

    const row_class_rules = useMemo(() => ({
        'row-selectable': () => !!onRowSelect
    }), [onRowSelect]);

    return (
        <div className={classNames("w-full flex flex-col", full_height ? "h-full" : "")}>
            {/* Header with actions */}
            {show_actions_header && <div className="flex-shrink-0 flex justify-between items-center mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="text-sm text-gray-600 font-medium flex items-center gap-2">
                    {`${total_rows?.toLocaleString() || "N/A"} total rows`}
                </div>
                <div className="flex gap-2">
                    {!readonly && onAddRow && (
                        <Button
                            icon={PlusCircleIcon}
                            text="Add Row"
                            onClick={addNewRow}
                        />
                    )}
                </div>
            </div>}

            <div
                className={classNames(
                    "transition-all duration-300 ease-in-out overflow-hidden",
                    display_filters.length > 0
                        ? "opacity-100 max-h-16 mb-4"
                        : "opacity-0 max-h-0 mb-0"
                )}
            >
                <div className="flex flex-row items-center gap-2">
                    <span className="pl-2 text-sm text-gray-400">Filters:</span>
                    <div className="flex-grow">
                        <MultiselectInputField
                            values={display_filters.map(filter => `'${filter.column_name}' ${filter.value}`)}
                            placeholder="No filters applied"
                            separator="space"
                            color="sky"
                            onRemove={handleRemoveFilter}
                            no_border={true}
                        />
                    </div>
                </div>
            </div>

            {/* AG Grid */}
            <div className={classNames(
                "ag-theme-quartz relative w-full",
                full_height ? "flex-1" : "",
                full_height ?
                    (show_actions_header ? "min-h-[320px]" : "min-h-[400px]") :
                    (show_actions_header ? "h-[320px]" : "h-[400px]")
            )}>
                {/* Custom loading overlay */}
                {(is_loading || !can_render) && (
                    <div className="absolute top-7 left-0 right-0 bottom-0 bg-white bg-opacity-75 flex items-center justify-center z-[5]">
                        <div className="flex flex-col items-center gap-3">
                            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-sm text-gray-600">Loading rows...</span>
                        </div>
                    </div>
                )}

                {/* Custom no data overlay */}
                {show_no_data && !is_loading && (
                    <div className="absolute top-7 left-0 right-0 bottom-0 bg-white bg-opacity-95 flex items-center justify-center z-[5]">
                        <div className="flex flex-col items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                                <TableCellsIcon className="w-6 h-6 text-gray-400" />
                            </div>
                            <span className="text-lg text-gray-500 font-medium">No data matches selected filters</span>
                            <span className="text-sm text-gray-400">Try adjusting filters or load additional data to knowledge base</span>
                        </div>
                    </div>
                )}

                {can_render && <AgGridReact
                    columnDefs={column_defs}
                    onGridReady={onGridReady}
                    onCellEditingStopped={onCellEditingStopped}
                    onFilterChanged={onFilterChanged}
                    onFirstDataRendered={onFirstDataRendered}
                    onRowClicked={onRowClicked}
                    rowClassRules={row_class_rules}
                    defaultColDef={{
                        sortable: true,
                        filter: true,
                        resizable: true,
                        floatingFilter: false,
                    }}
                    domLayout="normal"
                    rowModelType="infinite"
                    cacheBlockSize={page_size}
                    cacheOverflowSize={2}
                    maxConcurrentDatasourceRequests={2}
                    infiniteInitialRowCount={page_size}
                    maxBlocksInCache={10}
                    animateRows={true}
                    enableCellTextSelection={true}
                    ensureDomOrder={true}
                    suppressMenuHide={true}
                    getRowId={(params) => params.data.uuid}
                />}
            </div>

            {/* Delete confirmation modal */}
            <ConfirmModal
                open={show_delete_confirm}
                title="Delete Row"
                message={["Are you sure you want to delete this row? This action cannot be undone."]}
                confirm="Delete"
                onClose={onDeleteConfirm}
            />
        </div>
    );
}